"use client"

import * as React from "react"
import { useTranslations } from "@/i18n"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"

import {
  createBranchFormSchema,
  suggestBranchCode,
  type BranchFormInput,
} from "@/features/church/config/branches/schemas/branch-form.schema"
import { BranchStatus, type Branch } from "@/features/church/config/branches/types/branch.types"
import {
  BranchFormFields,
  BranchMobileFormFields,
} from "@/features/church/config/branches/components/BranchFormFields"
import { useCustomFieldTemplatesStore } from "@/features/church/config/custom-fields"
import { CustomFieldEntity, CustomFieldValueType } from "@/shared/lib/attributes/custom-fields"
import {
  DEFAULT_DOCUMENT_LANGUAGE,
  DEFAULT_DOCUMENT_TIMEZONE,
  isCuratedDocumentTimezone,
} from "@ibada/shared-types/workspace"
import { useCurrentWorkspaceAccess } from "@/features/auth/hooks/use-current-workspace-access"
import { useApplyApiFieldErrors, useApiFormBannerMessage } from "@/shared/lib/api-errors/api-form-errors"
import { getApiFormErrorDetails } from "@/shared/lib/api-errors/form-api-errors"
import { Button } from "@ibada/ui"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@ibada/ui"
import { Form } from "@ibada/ui"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { MobileDetailScreen } from "@/shared/components/mobile-detail-screen"

type BranchFormModalProps = {
  open: boolean
  mode: "create" | "edit"
  initialBranch?: Branch | null
  isSubmitting: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (values: BranchFormInput) => Promise<void>
}

const defaultValues: BranchFormInput = {
  name: "",
  code: "",
  details: "",
  phoneNumber: "",
  principalMemberId: "",
  language: DEFAULT_DOCUMENT_LANGUAGE,
  timezone: DEFAULT_DOCUMENT_TIMEZONE,
  status: BranchStatus.ACTIVE,
  // Off by default: administering a branch is a decision, not a side effect of creating one.
  grantCreatorAccess: false,
}

const BRANCH_FIELD_NAME_MAP: Partial<Record<string, keyof BranchFormInput | null>> = {
  principal: "principalMemberId",
  principalMemberId: "principalMemberId",
  phone: "phoneNumber",
  phoneNumber: "phoneNumber",
  detail: "details",
  details: "details",
  description: "details",
}

export function BranchFormModal({
  open,
  mode,
  initialBranch,
  isSubmitting,
  onOpenChange,
  onSubmit,
}: BranchFormModalProps) {
  const t = useTranslations("churchConfig.branches.form")
  const applyFieldErrors = useApplyApiFieldErrors<BranchFormInput>("churchConfig.branches")
  const formBannerMessage = useApiFormBannerMessage("churchConfig.branches")
  const isMobile = useIsMobile()
  const templates = useCustomFieldTemplatesStore((state) => state.templates)
  const principalTemplate = templates.find(
    (template) =>
      template.entity === CustomFieldEntity.BRANCH &&
      template.valueType === CustomFieldValueType.MEMBER &&
      template.label.trim().toLowerCase() === "responsable principal"
  )
  // Pre-fill language + timezone from the active workspace so branches
  // inherit the workspace defaults by default; the user can still
  // override them per branch at create or edit time.
  const workspaceAccess = useCurrentWorkspaceAccess()
  const workspaceLanguage = (workspaceAccess?.workspace?.defaultLanguage === "en" ? "en" : "fr") as BranchFormInput["language"]
  const workspaceTimezoneRaw = workspaceAccess?.workspace?.timezone?.trim()
  const workspaceTimezone = workspaceTimezoneRaw && isCuratedDocumentTimezone(workspaceTimezoneRaw)
    ? workspaceTimezoneRaw
    : DEFAULT_DOCUMENT_TIMEZONE
  const schema = React.useMemo(
    () =>
      createBranchFormSchema({
        nameMin: t("errors.nameMin"),
        nameMax: t("errors.nameMax"),
        detailsMax: t("errors.detailsMax"),
        phoneInvalid: t("errors.phoneInvalid"),
        codeInvalid: t("errors.codeInvalid"),
      }),
    [t]
  )

  const form = useForm<BranchFormInput>({
    resolver: zodResolver(schema),
    defaultValues,
  })
  const nameValue = form.watch("name")
  const codeTouchedRef = React.useRef(false)

  React.useEffect(() => {
    if (!open) return

    if (mode === "edit" && initialBranch) {
      form.reset({
        name: initialBranch.name,
        code: initialBranch.code ?? "",
        details: initialBranch.details ?? "",
        phoneNumber: initialBranch.phoneNumber ?? "",
        principalMemberId: initialBranch.principalMemberId ?? "",
        language: (initialBranch.language === "en" ? "en" : "fr") as BranchFormInput["language"],
        timezone:
          initialBranch.timezone && isCuratedDocumentTimezone(initialBranch.timezone)
            ? initialBranch.timezone
            : workspaceTimezone,
        status: initialBranch.status ?? BranchStatus.ACTIVE,
        // Only meaningful while CREATING — editing a branch never re-grants anything.
        grantCreatorAccess: false,
      })
      codeTouchedRef.current = true
      return
    }

    form.reset({
      ...defaultValues,
      language: workspaceLanguage,
      timezone: workspaceTimezone,
    })
    codeTouchedRef.current = false
  }, [form, initialBranch, mode, open, workspaceLanguage, workspaceTimezone])

  React.useEffect(() => {
    if (!open || codeTouchedRef.current) return
    const suggested = suggestBranchCode(nameValue ?? "")
    if (suggested) {
      form.setValue("code", suggested, { shouldValidate: true })
    }
  }, [form, nameValue, open])

  const handleSubmit = form.handleSubmit(async (values) => {
    form.clearErrors()

    try {
      await onSubmit(values)
    } catch (error) {
      applyFieldErrors(form.setError, error, { fieldNameMap: BRANCH_FIELD_NAME_MAP })
      if (Object.keys(getApiFormErrorDetails(error).fieldErrors).length > 0) return
      form.setError("root.serverError", { type: "server", message: formBannerMessage(error) })
    }
  })

  const principalLabel = principalTemplate?.label ?? t("fields.principal")

  if (isMobile) {
    // Phone: a full screen (Annuler / title / Créer); language, timezone and principal open pickers.
    return (
      <MobileDetailScreen
        open={open}
        onOpenChange={onOpenChange}
        title={mode === "create" ? t("createTitle") : t("editTitle")}
        backLabel={t("cancel")}
        showBackChevron={false}
        action={{
          label: mode === "create" ? t("createSubmit") : t("editSubmit"),
          disabled: isSubmitting,
          onPress: () => void handleSubmit(),
        }}
      >
        <BranchMobileFormFields
          form={form}
          mode={mode}
          principalLabel={principalLabel}
          principalName={initialBranch?.principalMember?.name}
        />
      </MobileDetailScreen>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent fullHeight size='2xl' data-testid={`branch-form-modal-${mode}`}>
        <DialogHeader>
          <DialogTitle>{mode === "create" ? t("createTitle") : t("editTitle")}</DialogTitle>
          <DialogDescription>
            {mode === "create" ? t("createDescription") : t("editDescription")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className='space-y-4' onSubmit={handleSubmit} data-testid={`branch-form-${mode}`}>
            <BranchFormFields control={form.control} mode={mode} principalLabel={principalLabel} />

            {form.formState.errors.root?.serverError?.message ? (
              <p className='text-destructive text-sm'>{form.formState.errors.root.serverError.message}</p>
            ) : null}

            <DialogFooter>
              <Button type='button' variant='outline' onClick={() => onOpenChange(false)} data-testid='branch-form-cancel-button'>
                {t("cancel")}
              </Button>
              <Button type='submit' loading={isSubmitting} data-testid='branch-form-submit-button'>
                {mode === "create" ? t("createSubmit") : t("editSubmit")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
